import React, { useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import Container from '@material-ui/core/Container'
import EventAvailableIcon from '@material-ui/icons/EventAvailable'
import EventBusyOutlinedIcon from '@material-ui/icons/EventBusyOutlined'
import AddToPhotosOutlinedIcon from '@material-ui/icons/AddToPhotosOutlined'
import LinkOutlinedIcon from '@material-ui/icons/LinkOutlined'
import RoomOutlinedIcon from '@material-ui/icons/RoomOutlined'
import Grid from '@material-ui/core/Grid'
import Box from '@mui/material/Box'
import LinearProgress from '@material-ui/core/LinearProgress'
import Switch from '@material-ui/core/Switch'
import TimerIcon from '@material-ui/icons/Timer'
import WebOutlinedIcon from '@material-ui/icons/WebOutlined'
import Typography from '@material-ui/core/Typography'
import Link from '@material-ui/core/Link'
import Button from '@material-ui/core/Button'
import { useAuth } from 'lib/auth'
import useStyles, {
  SettingsBlock,
  SettingsFormControlLabel,
} from './settings.styles'
import SettingsBox from 'components/coach-settings-box'
import Error from 'components/error'
import { PageHeader } from 'components/page-header'
import { usePortal } from 'lib/portal'
import { useSnackbar, useSnackbarError } from 'lib/snackbar'
import SocialShareDialog from 'components/social-share-dialog'
import { gtmEvent } from 'lib/gtm'
import { useCreateSubscriptionPaymentSession } from 'lib/graphql/mutations/create-subscription-payment-session'
import { COACH_PREFERENCES, UPDATE_COACH_PREF } from './queries'
import { MY_PGA_COM_HOST } from 'env'
import { ReactComponent as AutoRespondersIcon } from 'images/icons/auto-responders.svg'
import { ReactComponent as GoogleCalendarIcon } from 'images/icons/google-calendar-colored.svg'
import { useFeatureFlags, flags } from 'lib/feature-flags'

const buildScheduleBlocks = ({ isCustomAvailabilityEnabled }) => {
  const calendarBlock = isCustomAvailabilityEnabled
    ? {
      link: 'settings/availability',
      icon: <EventAvailableIcon />,
      title: 'Availability Settings',
      subtitle:
        'Manage your weekly availability, one-time openings, and blocked times in one place.',
    }
    : {
      link: 'settings/availability',
      icon: <EventAvailableIcon />,
      title: 'Availability',
      subtitle:
        'Determine when you will be available to coach your students during the week at each location.',
    }

  const blocks = [calendarBlock]

  if (!isCustomAvailabilityEnabled) {
    blocks.push({
      link: 'settings/blocked-times',
      icon: <EventBusyOutlinedIcon />,
      title: 'Blocked times',
      subtitle:
        'Override your availability by blocking busy times on your calendar.',
    })
  }

  blocks.push({
    link: 'settings/booking-limits',
    icon: <TimerIcon />,
    title: 'Booking & Cancellation Rules',
    subtitle:
      'Set your booking and cancellation limits to control when students can book sessions with you and if they can cancel them.',
  })

  return blocks
}

const programBlocks = [
  {
    link: 'bookings/offerings',
    icon: <AddToPhotosOutlinedIcon />,
    title: 'Manage Programs',
    subtitle: 'Continue to set up your PGA Schedule',
  },
]

const ToggleSwitch = ({
  checked,
  enabled,
  mutation,
  eventCategory,
  formAction,
}) => {
  const [state, setState] = useState(checked)
  const [toggle] = useMutation(mutation)
  const { enqueueSnackbar } = useSnackbar()

  return (
    <Switch
      checked={state}
      disabled={!enabled}
      onChange={event => {
        toggle({
          variables: { input: { [formAction]: event.target.checked } },
        }).then(({ data: { updateCoachPreferences: op } }) => {
          gtmEvent({
            event: 'formSubmit',
            formCategory: eventCategory,
            formAction: op.coach.preferences[formAction]
              ? `${formAction}-enable`
              : `${formAction}-disable`,
          })
          enqueueSnackbar(op.message, {
            variant: op.success ? 'success' : 'error',
          })
          setState(
            op.coach.preferences[
              formAction === 'bookNow' ? 'bookNowEnabled' : formAction
            ],
          )
        })
      }}
    />
  )
}

const SettingsPage = () => {
  const { user } = useAuth()
  const [isPushSyncEnabled, isCustomAvailabilityEnabled] = useFeatureFlags([
    flags.FLAG_CALENDAR_PUSH_SYNC,
    flags.FLAG_CUSTOM_AVAILABILITY,
  ])
  const scheduleBlocks = buildScheduleBlocks({ isCustomAvailabilityEnabled })

  const [target] = usePortal()
  const styles = useStyles()
  const { enqueueSnackbar } = useSnackbar()
  const snackbarError = useSnackbarError(enqueueSnackbar)
  const successUrl = `${MY_PGA_COM_HOST}/pga-coach/settings`
  const [
    callCreateSubscriptionPaymentSession,
    { loading: loadingPaymentSession },
  ] = useCreateSubscriptionPaymentSession()

  const handleUpgradeClick = async () => {
    try {
      const { data } = await callCreateSubscriptionPaymentSession({
        variables: { successUrl },
      })
      const {
        url,
        success,
        message,
      } = data?.createPgaCoachSubscriptionPaymentSession
      if (!success) {
        return snackbarError(message)
      }
      if (success && url) {
        window.location.href = url
      }
    } catch (e) {
      snackbarError(e?.message)
    }
  }

  const { data, loading, error } = useQuery(COACH_PREFERENCES)

  if (loading) return <LinearProgress color="secondary" />
  if (error) return <Error error={error} />

  const { freeTierLimitsReached, preferences } = data?.currentUser?.attributes
  const enableContactButton = !freeTierLimitsReached.includes('LEADS')

  return (
    <>
      <PageHeader title="Settings" actions={target} />
      <Container className={styles.root}>
        <>
          <Typography variant="h6" gutterBottom className={styles.heading}>
            Leads
          </Typography>
          <Grid item lg={7} md={8} xs={12}>
            <RouterLink
              className={styles.mainWrapper}
              to="settings/leads-auto-reply"
            >
              <SettingsBox
                icon={
                  <div className={styles.icon}>
                    <AutoRespondersIcon
                      style={{
                        fill: 'currentColor',
                        color: 'inherit',
                        width: 24,
                        height: 24,
                      }}
                    />
                  </div>
                }
                title="Leads Auto-Reply"
                subtitle={
                  preferences?.leadAutoReplyEnabled
                    ? 'Auto-reply is on'
                    : 'Auto-reply is off'
                }
                action={<ChevronRightIcon className={styles.action} />}
              />
            </RouterLink>
          </Grid>
        </>
        <Typography variant="h6" gutterBottom className={styles.heading}>
          Schedule
        </Typography>
        <Grid container spacing={2}>
          {scheduleBlocks.map(block => (
            <Grid item lg={7} md={8} xs={12} key={block.title}>
              <RouterLink className={styles.mainWrapper} to={block.link}>
                <SettingsBox
                  icon={<div className={styles.icon}>{block.icon}</div>}
                  title={block.title}
                  subtitle={block.subtitle}
                  action={<ChevronRightIcon className={styles.action} />}
                />
              </RouterLink>
            </Grid>
          ))}
          {isPushSyncEnabled && (
            <Grid item lg={7} md={8} xs={12}>
              <RouterLink
                className={styles.mainWrapper}
                to="settings/google-calendar"
              >
                <SettingsBox
                  icon={
                    <div className={styles.icon}>
                      <GoogleCalendarIcon style={{ width: 24, height: 24 }} />
                    </div>
                  }
                  title="Google Calendar"
                  subtitle="Connect Google to sync blocked times and push events to your calendar."
                  action={<ChevronRightIcon className={styles.action} />}
                />
              </RouterLink>
            </Grid>
          )}
        </Grid>

        <Typography variant="h6" gutterBottom className={styles.heading}>
          Coach Profile
        </Typography>
        <Grid container spacing={2}>
          <Grid item lg={7} md={8} xs={12}>
            <RouterLink className={styles.mainWrapper} to="settings/facilities">
              <SettingsBox
                icon={
                  <div className={styles.icon}>
                    <RoomOutlinedIcon />
                  </div>
                }
                title="Manage Facilities"
                subtitle="Manage the facilities that are showcased on your profile. Your primary facility and its address is used for location-based searches on pga.com."
                action={<ChevronRightIcon className={styles.action} />}
              />
            </RouterLink>
          </Grid>
          <Grid item lg={7} md={8} xs={12}>
            <SettingsBlock className={styles.settingsDescription}>
              <SettingsFormControlLabel
                label={
                  <Typography variant="subtitle1">
                    Display the “Book Now” button on your profile
                  </Typography>
                }
                control={
                  <ToggleSwitch
                    checked={preferences?.bookNowEnabled}
                    enabled={true}
                    mutation={UPDATE_COACH_PREF}
                    eventCategory="book-now"
                    formAction="bookNow"
                  />
                }
              />
              <Typography variant="body2" className={styles.settingsText}>
                When enabled, golfers will see a “Book Now” option on your Coach
                Profile whenever you have active offerings or events on PGA
                Coach. Additional booking behaviors may be configured in your
                Book Now Preferences.
              </Typography>
              <Typography variant="body2" className={styles.settingsText}>
                When disabled, golfers are able to message you from your
                Profile, but bookings remain private unless you share your
                Booking Link with them.
              </Typography>
            </SettingsBlock>
          </Grid>
          <Grid item lg={7} md={8} xs={12}>
            <SettingsBlock className={styles.settingsDescription}>
              <SettingsFormControlLabel
                label={
                  <Typography variant="subtitle1">
                    Display the “Contact” button on your profile
                  </Typography>
                }
                control={
                  <ToggleSwitch
                    checked={enableContactButton && preferences?.acceptsLeads}
                    enabled={enableContactButton}
                    mutation={UPDATE_COACH_PREF}
                    eventCategory="accept-leads"
                    formAction="acceptsLeads"
                  />
                }
              />
              {!enableContactButton && !loadingPaymentSession && (
                <Link
                  underline="always"
                  className={`${styles.linkButton} ${styles.settingsText}`}
                  component={Button}
                  onClick={handleUpgradeClick}
                >
                  Upgrade your plan to accept more than 2 leads
                </Link>
              )}
              {loadingPaymentSession && <LinearProgress />}
              <Typography variant="body2" className={styles.settingsText}>
                When enabled, golfers will see a “Contact” option on your Coach
                Profile. This means you can accept leads.
              </Typography>
              <Typography variant="body2" className={styles.settingsText}>
                When disabled, golfers will not be able to become a lead and the
                contact form on your Coach Profile is removed.
              </Typography>
            </SettingsBlock>
          </Grid>
          <Grid item lg={7} md={8} xs={12}>
            <RouterLink
              className={styles.mainWrapper}
              to="settings/booking-url"
            >
              <SettingsBox
                icon={
                  <div className={styles.icon}>
                    <WebOutlinedIcon />
                  </div>
                }
                title='"Book Now" Preferences'
                subtitle="Determine the system you would like to use to collect and manage registrations for your programs."
                action={<ChevronRightIcon className={styles.action} />}
              />
            </RouterLink>
          </Grid>
        </Grid>

        {/* Your Programs Section */}
        <Typography variant="h6" gutterBottom className={styles.heading}>
          Your Programs
        </Typography>
        <Grid container spacing={2}>
          {programBlocks.map(block => (
            <Grid item lg={7} md={8} xs={12} key={block.title}>
              <RouterLink className={styles.mainWrapper} to={block.link}>
                <SettingsBox
                  icon={<div className={styles.icon}>{block.icon}</div>}
                  title={block.title}
                  subtitle={block.subtitle}
                  action={<ChevronRightIcon className={styles.action} />}
                />
              </RouterLink>
            </Grid>
          ))}
          <Grid item lg={7} md={8} xs={12} className={styles.clickable}>
            <SocialShareDialog
              trigger={
                <Box className={styles.settingsWrapper}>
                  <div className={styles.icon}>
                    <LinkOutlinedIcon />
                  </div>
                  <Box className={styles.settingsCaption}>
                    <Typography variant="subtitle1">
                      Share Booking Link with Your Students
                    </Typography>
                    <Typography variant="body2">
                      Copy your Coach Offerings link and share it via text,
                      email, or social media.
                    </Typography>
                  </Box>
                </Box>
              }
              shareUrl={
                data?.currentUser?.attributes?.bookingUrl ||
                user?.coach?.bookingUrl
              }
              shareTitle="My Offerings"
              dialogTitle="Share this booking link with your students"
              showBookingUrl={true}
              att="booking-link"
            />
          </Grid>
        </Grid>
      </Container>
    </>
  )
}

export default SettingsPage
